import { useEffect, useRef, useState } from 'react';
import { storage } from './storage.js';

/**
 * Entwürfe (Paket P3): halb Getipptes überlebt Neuladen, Absturz und Tab-Schließen. Je Person getrennt, damit auf
 * einem geteilten Gerät niemand die Entwürfe anderer sieht. Entwürfe älter als 30 Tage werden aufgeräumt.
 */
let user: string | null = null;
const PREFIX = 'werft.draft.';
const MAX_AGE = 30 * 86_400_000;

export const draftUser = () => user;

export function setDraftUser(id: string | null) {
  user = id;
  if (id) prune();
}

const keyOf = (k: string) => `${PREFIX}${user}.${k}`;

export interface Draft<T> {
  value: T;
  at: number;
}

export const drafts = {
  get<T>(k: string): T | undefined {
    if (!user) return undefined;
    return storage.get<Draft<T>>(keyOf(k))?.value;
  },
  /**
   * Nachprüfung 0.167.1 R04: liefert, ob DIESER Schreibversuch dauerhaft gelang (Speicher voll/gesperrt → `false`).
   * Ein erfolgreicher Test beim Start sagt nichts über spätere Schreibversuche.
   */
  set<T>(k: string, value: T): boolean {
    if (!user) return false;
    return storage.set(keyOf(k), { value, at: Date.now() } satisfies Draft<T>);
  },
  clear(k: string) {
    if (user) storage.remove(keyOf(k));
  },
  /** Text an einen Entwurf anhängen (z. B. abgelehnte Eingabe zurückgeben) und offene Felder benachrichtigen */
  append(k: string, text: string) {
    const cur = drafts.get<string>(k);
    const next = cur ? `${cur} · ${text}` : text;
    drafts.set(k, next);
    listeners.get(k)?.forEach((l) => l(next));
  },
};

const listeners = new Map<string, Set<(v: string) => void>>();

function prune() {
  try {
    const now = Date.now();
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (!k?.startsWith(PREFIX)) continue;
      const d = storage.get<Draft<unknown>>(k);
      if (!d || now - d.at > MAX_AGE) storage.remove(k);
    }
  } catch {
    /* egal */
  }
}

/** Eingabefeld mit Entwurf: startet mit dem gespeicherten Text, schreibt jede Änderung mit */
export function useDraftText(key: string): [string, (v: string) => void] {
  const [value, setValue] = useState(() => drafts.get<string>(key) ?? '');
  const k = useRef(key);
  useEffect(() => {
    if (k.current !== key) {
      k.current = key;
      setValue(drafts.get<string>(key) ?? '');
    }
  }, [key]);
  useEffect(() => {
    const set = listeners.get(key) ?? new Set();
    listeners.set(key, set);
    const l = (v: string) => setValue(v);
    set.add(l);
    return () => {
      set.delete(l);
    };
  }, [key]);
  const set = (v: string) => {
    setValue(v);
    if (v) drafts.set(key, v);
    else drafts.clear(key);
  };
  return [value, set];
}
